import React, { useCallback, useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import {
  FiAlertCircle,
  FiCheck,
  FiCopy,
  FiExternalLink,
  FiX,
} from 'react-icons/fi';

import { getAdminApplications, reviewApplication } from '../../api/client';
import { formatCurrency, formatDate, initials } from '../../utils/format';

const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

/**
 * Shown once, after an approval. The temporary password is never stored in
 * plain text and cannot be retrieved again — if it is lost, the clinician has
 * to use the password reset flow.
 */
function ApprovalResult({ result, onDismiss }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        `${result.account.email} / ${result.account.temporaryPassword}`
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="mm-approval" role="status">
      <div className="mm-approval__head">
        <FiCheck size={16} aria-hidden="true" />
        <strong>Approved &mdash; account created</strong>
      </div>
      <p className="mm-approval__text">
        {result.therapist.name} is now listed, and a portal account has been
        provisioned. <strong>These credentials are shown once.</strong>
      </p>
      <div className="mm-approval__creds">
        <code>{result.account.email}</code>
        <code>{result.account.temporaryPassword}</code>
        <button type="button" className="mm-approval__copy" onClick={copy}>
          <FiCopy size={13} aria-hidden="true" />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <Button variant="outline-ink" size="sm" onClick={onDismiss}>
        Dismiss
      </Button>
    </div>
  );
}

function ApplicationCard({ application, onReview, busy }) {
  const a = application;
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);

  return (
    <article className="mm-application">
      <div className="mm-application__head">
        <span className="mm-person__avatar" aria-hidden="true">
          {initials(a.fullName)}
        </span>
        <div className="mm-application__ident">
          <h3>{a.fullName}</h3>
          <span>
            {a.title} &middot; {a.yearsExperience} yrs &middot; {a.location}
          </span>
        </div>
        <span className={`mm-status mm-status--${
          a.status === 'approved'
            ? 'completed'
            : a.status === 'rejected'
            ? 'no-show'
            : 'scheduled'
        }`}>
          {a.status}
        </span>
      </div>

      <dl className="mm-application__facts">
        <div>
          <dt>Reference</dt>
          <dd>{a.reference}</dd>
        </div>
        <div>
          <dt>Registration</dt>
          <dd>
            {a.registrationBody} {a.registrationNumber}
          </dd>
        </div>
        <div>
          <dt>Fee</dt>
          <dd>{formatCurrency(a.fee, 'INR')}</dd>
        </div>
        <div>
          <dt>Submitted</dt>
          <dd>{formatDate(a.submittedAt)}</dd>
        </div>
        <div>
          <dt>Contact</dt>
          <dd>
            <a href={`mailto:${a.email}`}>{a.email}</a>
            {a.phone && <> &middot; {a.phone}</>}
          </dd>
        </div>
        {a.website && (
          <div>
            <dt>Website</dt>
            <dd>
              <a href={a.website} target="_blank" rel="noopener noreferrer">
                {a.website.replace(/^https?:\/\//, '')}
                <FiExternalLink size={11} className="ms-1" aria-hidden="true" />
              </a>
            </dd>
          </div>
        )}
      </dl>

      <div className="mm-application__tags">
        {a.specialties.map((s) => (
          <span className="mm-pill" key={s}>
            {s}
          </span>
        ))}
        {a.modalities.map((m) => (
          <span className="mm-pill" key={m}>
            {m}
          </span>
        ))}
      </div>

      <button
        type="button"
        className="mm-application__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? 'Hide' : 'Read'} their introduction
      </button>
      {open && <p className="mm-application__bio">{a.bio}</p>}

      {a.reviewerNote && (
        <p className="mm-application__note">
          <strong>Review note:</strong> {a.reviewerNote}
        </p>
      )}

      {a.status === 'pending' && (
        <div className="mm-application__actions">
          <label className="mm-sr-only" htmlFor={`note-${a.id}`}>
            Review note
          </label>
          <input
            id={`note-${a.id}`}
            className="mm-application__note-input"
            placeholder="Note (e.g. how you verified the registration)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="mm-application__buttons">
            <Button
              size="sm"
              disabled={busy}
              onClick={() => onReview(a.id, 'approved', note)}
            >
              {busy ? (
                <Spinner animation="border" size="sm" aria-hidden="true" />
              ) : (
                <>
                  <FiCheck size={14} className="me-1" aria-hidden="true" />
                  Approve &amp; create account
                </>
              )}
            </Button>
            <Button
              variant="outline-ink"
              size="sm"
              disabled={busy}
              onClick={() => onReview(a.id, 'rejected', note)}
            >
              <FiX size={14} className="me-1" aria-hidden="true" />
              Reject
            </Button>
          </div>
        </div>
      )}
    </article>
  );
}

export default function ApplicationsPanel() {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [approval, setApproval] = useState(null);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const result = await getAdminApplications(status);
      setRows(result.applications);
      setCounts(result.counts);
      setError(null);
    } catch (err) {
      setError(err.message || 'Could not load applications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  const handleReview = async (id, status, note) => {
    setBusyId(id);
    setError(null);
    try {
      const result = await reviewApplication(id, status, note);
      if (status === 'approved') setApproval(result);
      await load(tab);
    } catch (err) {
      setError(err.message || 'Could not record that decision.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="mm-applications">
      <div className="mm-portal__toolbar">
        <div className="mm-portal__tabs" role="tablist" aria-label="Filter applications">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`mm-portal__tab ${tab === t.key ? 'mm-portal__tab--active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              {counts[t.key] != null && t.key !== 'all' && (
                <span className="mm-portal__tab-count">{counts[t.key]}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mm-form-alert mm-form-alert--error" role="alert">
          <FiAlertCircle size={16} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {approval && (
        <ApprovalResult result={approval} onDismiss={() => setApproval(null)} />
      )}

      <div className="mm-applications__list">
        {loading &&
          Array.from({ length: 2 }, (_, i) => (
            <div className="mm-skeleton" key={`ska-${i}`} style={{ height: 140 }}>
              <div className="mm-skeleton__bar" style={{ width: '35%' }} />
              <div className="mm-skeleton__bar" style={{ width: '60%' }} />
            </div>
          ))}

        {!loading && rows.length === 0 && (
          <div className="mm-portal__empty">
            <strong>No {tab === 'all' ? '' : tab} applications.</strong>
            <span>
              {tab === 'pending'
                ? 'Nothing is waiting on review right now.'
                : 'Nothing to show for this filter.'}
            </span>
          </div>
        )}

        {!loading &&
          rows.map((a) => (
            <ApplicationCard
              key={a.id}
              application={a}
              onReview={handleReview}
              busy={busyId === a.id}
            />
          ))}
      </div>
    </section>
  );
}
